import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Building2,
  ChevronDown,
  Download,
  Eye,
  Share,
  User,
  X,
} from 'lucide-react';
import { TABBIO_MARK_PATH } from '../common/TabbioMark';
import { PremiumMark } from '../subscription/PremiumMark';
import { HugeIcon } from '../icons/HugeIcon';
import { ShareIcon } from '../icons/ShareIcon';
import { TRAFFIC_SOURCE_HUGEICONS } from './traffic-source-icons';
import type { TrafficSourceIconKey } from '../../lib/cv-analytics-labels';
import {
  actorInitials,
  CV_ANALYTICS_ACTIVITY_PAGE,
  CV_ANALYTICS_RANGES,
  CV_ANALYTICS_STACK_SIZE,
  selectStackActors,
  type CvAnalyticsMetric,
} from '../../lib/cv-analytics-view';
import { usePublicProfileAnalyticsActor } from '../../hooks/useAnalyticsApi';
import { WebDialog } from '../common/WebDialog.web';
import type {
  CvAnalyticsActivityView,
  CvAnalyticsModel,
  CvAnalyticsScope,
} from './useCvAnalytics';

/**
 * Web pieces of the CV analytics quick look (CvAnalyticsSheet.web) and full
 * screen (PersonalAnalyticsScreen.web), from the 4 Oct 2026 Free/Premium
 * handoff (`analytics.css` in the subscription journeys reference). Native
 * twins live in cv-analytics-hub.tsx; both draw from useCvAnalytics.
 */
export function CvAnalyticsStyles() {
  return (
    <style>{`
      .cva {
        --cva-text: #171719;
        --cva-muted: #686870;
        --cva-line: #e5e5eb;
        --cva-fill: #f3f3f6;
        --cva-accent: #5a2aff;
        --cva-green: #187a44;
        --cva-menu: #ffffff;
        --cva-surface: var(--bg);
        color: var(--cva-text);
        font-family: 'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif;
        text-align: start;
      }
      [data-theme="dark"] .cva {
        --cva-text: #f4f4f7;
        --cva-muted: #acacb6;
        --cva-line: #3d3d44;
        --cva-fill: #242427;
        --cva-accent: #ac93ff;
        --cva-green: #89dab1;
        --cva-menu: #1c1c1f;
      }
      .cva *, .cva *::before, .cva *::after { box-sizing: border-box; }
      .cva button, .cva select {
        font: inherit;
        color: inherit;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }
      .cva button { border: 0; background: none; min-height: 44px; padding: 0; }
      .cva svg { display: block; flex-shrink: 0; }
      .cva button:focus-visible, .cva select:focus-visible, .cva-bars:focus-visible {
        outline: 2px solid var(--cva-accent);
        outline-offset: 4px;
      }
      .cva-sr-only {
        position: absolute !important;
        width: 1px;
        height: 1px;
        padding: 0;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
      }

      .cva-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
      .cva-header h1 { flex: 1; min-width: 0; margin: 0; font-size: 30px; line-height: 1.2; font-weight: 700; letter-spacing: -1.1px; }
      .cva-popout .cva-header h1 { font-size: 23px; letter-spacing: -0.6px; }
      .cva .cva-close {
        width: 44px;
        border-radius: 50%;
        background: var(--cva-fill);
        color: var(--cva-muted);
        display: grid;
        place-items: center;
      }

      .cva-scope {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin: 10px 0 14px;
        font-size: 12px;
        color: var(--cva-muted);
      }
      .cva-popout .cva-scope { margin: 12px 0 24px; }
      .cva-scope-name { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; }
      .cva-scope-host { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

      .cva-filters { position: relative; flex: 0 1 auto; max-width: 48%; min-width: 48px; color: var(--cva-text); z-index: 5; }
      .cva .cva-filter-trigger {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
        width: 100%;
        font-size: 12px;
      }
      .cva-filter-trigger > span { text-align: end; line-height: 1.4; overflow-wrap: anywhere; }
      .cva-filter-menu {
        position: absolute;
        inset-inline-end: 0;
        width: 196px;
        max-width: calc(100vw - 64px);
        padding: 8px;
        background: var(--cva-menu);
        border: 1px solid var(--cva-line);
        box-shadow: 0 8px 30px #00000014;
        border-radius: 12px;
        overflow-y: auto;
        overscroll-behavior: contain;
        scrollbar-width: thin;
      }
      .cva-filter-label { display: block; padding: 5px 6px 3px; font-size: 10px; color: var(--cva-muted); }
      .cva-filter-group + .cva-filter-group { border-top: 1px solid var(--cva-line); margin-top: 3px; padding-top: 6px; }
      .cva-durations { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1px; margin: 4px 0 8px; }
      .cva .cva-duration {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 44px;
        padding: 4px;
        font-size: 11px;
        white-space: nowrap;
        border-radius: 6px;
        background: var(--cva-line);
      }
      .cva .cva-duration[aria-pressed="true"] { background: var(--cva-text); color: var(--cva-menu); }
      .cva .cva-duration:active { opacity: 0.7; transform: scale(0.97); }
      .cva-cv-row { display: flex; align-items: center; gap: 8px; min-height: 44px; padding-inline: 6px; font-size: 12px; color: var(--cva-text); }
      .cva .cva-cv-select {
        flex: 1;
        min-width: 0;
        min-height: 44px;
        margin: 0;
        border: 0;
        border-radius: 6px;
        padding-inline: 6px;
        background: var(--cva-menu);
        font-size: 12px;
        color: var(--cva-text);
      }
      .cva .cva-filter-reset {
        display: block;
        width: 100%;
        margin-top: 4px;
        padding: 8px;
        text-align: start;
        font-size: 12px;
        color: var(--cva-muted);
        border-top: 1px solid var(--cva-line);
      }

      .cva-metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; border-bottom: 1px solid var(--cva-line); }
      .cva .cva-metric {
        min-width: 0;
        padding: 0 0 15px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: start;
        position: relative;
        color: var(--cva-muted);
      }
      .cva-metric > span { font-size: 12px; }
      .cva-metric strong {
        color: var(--cva-text);
        font-size: 31px;
        line-height: 1.2;
        font-weight: 650;
        letter-spacing: -1px;
        margin-top: 7px;
        font-variant-numeric: tabular-nums;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .cva-popout .cva-metric strong { font-size: 30px; }
      .cva-trend { font-size: 11px; line-height: 1.4; min-height: 15px; color: var(--cva-green); margin-top: 7px; }
      .cva-trend.is-flat { color: var(--cva-muted); }
      .cva-metric[aria-pressed="true"] > span { color: var(--cva-text); font-weight: 600; }
      .cva-metric[aria-pressed="true"]::after {
        content: '';
        position: absolute;
        bottom: -1px;
        inset-inline-start: 0;
        height: 2px;
        width: 30px;
        border-radius: 2px;
        background: var(--cva-accent);
      }

      .cva-chart { margin: 22px 0; }
      .cva-popout .cva-chart { margin: 23px 0 24px; }
      .cva-bars {
        display: flex;
        gap: 5px;
        height: 104px;
        align-items: stretch;
        position: relative;
        cursor: crosshair;
        border-radius: 4px;
        touch-action: pan-y;
      }
      .cva-popout .cva-bars, .cva-popout .cva-chart-empty { height: 78px; }
      .cva-track { min-width: 0; flex: 1; display: flex; align-items: flex-end; }
      .cva-bar {
        width: 100%;
        border-radius: 5px 5px 3px 3px;
        background: color-mix(in srgb, var(--cva-accent) 85%, var(--cva-surface));
        transition: background 160ms ease, transform 160ms ease;
        transform-origin: bottom;
      }
      .cva-track.is-last .cva-bar { background: var(--cva-accent); }
      .cva-bars.is-selecting .cva-bar { background: color-mix(in srgb, var(--cva-accent) 85%, var(--cva-surface)); }
      .cva-bars .cva-track.is-selected .cva-bar { background: var(--cva-accent); transform: scaleY(1.03); }
      .cva-tip {
        position: absolute;
        top: -24px;
        transform: translateX(-50%);
        padding: 5px 8px;
        color: var(--cva-text);
        background: var(--cva-menu);
        box-shadow: 0 2px 15px #00000012;
        border: 1px solid var(--cva-line);
        font-size: 10px;
        border-radius: 7px;
        white-space: nowrap;
        pointer-events: none;
      }
      .cva-chart-empty { height: 104px; display: grid; place-items: center; font-size: 13px; color: var(--cva-muted); }
      .cva-axis { display: flex; justify-content: space-between; color: var(--cva-muted); font-size: 10px; padding-top: 10px; }

      .cva-avatar {
        position: relative;
        width: 40px;
        height: 40px;
        flex: 0 0 40px;
        display: grid;
        place-items: center;
        border-radius: 50%;
        background: var(--cva-fill);
        color: var(--cva-muted);
        border: 1px solid var(--cva-line);
        overflow: hidden;
      }
      .cva-avatar.is-stack {
        width: 32px;
        height: 32px;
        flex-basis: 32px;
        margin-inline-end: -8px;
        border: 2px solid var(--cva-surface);
        box-shadow: 0 0 0 1px var(--cva-line);
      }
      .cva-avatar.is-soft { filter: blur(1.5px); opacity: 0.7; }
      .cva-avatar.is-company { border-radius: 11px; background: var(--cva-surface); }
      .cva-avatar.is-stack.is-company { border-radius: 9px; }
      .cva-avatar.is-detail { width: 52px; height: 52px; flex-basis: 52px; }
      .cva-avatar.is-detail.is-company { border-radius: 14px; }
      .cva-avatar img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
      .cva-avatar.is-company img { inset: 20%; width: 60%; height: 60%; object-fit: contain; }
      .cva-avatar-initials { font-size: 13px; font-weight: 600; }
      .cva-avatar.is-stack .cva-avatar-initials { font-size: 11px; }

      .cva-detail { padding: 18px 24px max(24px, env(safe-area-inset-bottom)); overflow-y: auto; min-height: 0; }
      .cva-detail-header {
        position: sticky;
        top: -18px;
        z-index: 4;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-block: 4px;
        margin-bottom: 18px;
        background: var(--cva-surface);
        color: var(--cva-muted);
        font-size: 12px;
      }
      .cva-detail-identity { display: flex; align-items: center; gap: 14px; }
      .cva-detail-identity h2 { font-size: 22px; line-height: 1.2; letter-spacing: -0.5px; margin: 0; font-weight: 650; overflow-wrap: anywhere; }
      .cva-detail-identity p { font-size: 12px; color: var(--cva-muted); margin: 6px 0 0; }
      .cva-detail-scope { font-size: 12px; color: var(--cva-muted); margin: 22px 0 18px; }
      .cva-detail-metrics {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 10px;
        padding-bottom: 22px;
        border-bottom: 1px solid var(--cva-line);
      }
      .cva-detail-metrics strong { display: block; font-size: 28px; line-height: 1.2; font-weight: 650; letter-spacing: -0.5px; font-variant-numeric: tabular-nums; }
      .cva-detail-metrics span { display: block; font-size: 11px; color: var(--cva-muted); margin-top: 6px; }
      .cva-detail-note { font-size: 12px; color: var(--cva-muted); line-height: 1.6; margin: 16px 0; }
      .cva-detail-history h3 { font-size: 16px; margin: 22px 0 8px; font-weight: 600; }
      .cva-detail-history ul { list-style: none; padding: 0; margin: 0; }
      .cva-detail-history li { display: flex; align-items: center; gap: 12px; min-height: 64px; border-bottom: 1px solid var(--cva-line); }
      .cva-detail-history li:last-child { border: 0; }
      .cva-detail-history li > svg { color: var(--cva-muted); }
      .cva-detail-history li > span { flex: 1; min-width: 0; }
      .cva-detail-history strong { display: block; font-size: 13px; font-weight: 500; }
      .cva-detail-history small { display: block; font-size: 11px; color: var(--cva-muted); margin-top: 5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cva-detail-history time { font-size: 10px; color: var(--cva-muted); white-space: nowrap; }
      .cva-detail-footnote { font-size: 11px; color: var(--cva-muted); line-height: 1.5; margin: 20px 0 0; }
      .cva-detail-state { min-height: 160px; display: grid; place-items: center; gap: 10px; text-align: center; font-size: 13px; color: var(--cva-muted); }
      .cva .cva-detail-state button { background: var(--cva-fill); border-radius: 12px; padding: 0 20px; color: var(--cva-text); }

      .cva-activity h2, .cva-section h2 {
        font-size: 18px;
        font-weight: 650;
        letter-spacing: -0.4px;
        margin: 0 0 7px;
      }
      .cva-activity ul { list-style: none; padding: 0; margin: 0; }
      .cva-activity li {
        position: relative;
        display: flex;
        align-items: center;
        gap: 12px;
        min-height: 56px;
        border-bottom: 1px solid var(--cva-line);
      }
      .cva-activity li:last-child { border: 0; }
      .cva .cva-hit {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        z-index: 2;
        border-radius: 6px;
      }
      .cva-hit:hover { background: color-mix(in srgb, var(--cva-text) 3%, transparent); }
      .cva-hit:active { background: color-mix(in srgb, var(--cva-text) 6%, transparent); }
      .cva .cva-hit:focus-visible { outline-offset: -2px; }
      .cva-copy { display: flex; flex: 1; min-width: 0; flex-direction: column; gap: 4px; }
      .cva-copy strong { font-size: 13px; font-weight: 550; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cva-copy small { display: flex; align-items: center; gap: 3px; flex-wrap: wrap; font-size: 11px; line-height: 1.6; color: var(--cva-muted); }
      .cva .cva-cv-link::before {
        /* 44px-tall target without growing the row. */
        content: '';
        position: absolute;
        inset: -14px -4px;
      }
      .cva .cva-cv-link {
        position: relative;
        z-index: 3;
        min-height: 0;
        padding: 0;
        font-size: inherit;
        color: inherit;
        text-decoration: underline;
        text-decoration-color: var(--cva-line);
        text-underline-offset: 3px;
      }
      .cva-meta { display: flex; align-items: flex-end; flex-direction: column; gap: 5px; color: var(--cva-muted); }
      .cva-meta time { font-size: 10px; white-space: nowrap; }
      .cva .cva-more { width: 100%; display: flex; justify-content: center; align-items: center; gap: 4px; font-size: 12px; color: var(--cva-muted); }
      .cva-more svg { transition: transform 160ms ease; }
      .cva-more[aria-expanded="true"] svg { transform: rotate(180deg); }
      .cva-activity-empty { font-size: 12px; color: var(--cva-muted); padding: 20px 0; margin: 0; }
      .cva-privacy { margin: 9px 0 0; color: var(--cva-muted); font-size: 10px; text-align: center; line-height: 1.5; }

      .cva-locked { filter: blur(5px); opacity: 0.5; pointer-events: none; user-select: none; margin-top: 10px; }
      .cva-locked > div { display: flex; gap: 12px; align-items: center; height: 42px; }
      .cva-locked i { width: 40px; height: 40px; border-radius: 11px; background: var(--cva-muted); }
      .cva-locked span { flex: 1; }
      .cva-locked b, .cva-locked em { display: block; background: var(--cva-muted); height: 10px; width: 45%; border-radius: 5px; }
      .cva-locked em { width: 65%; margin-top: 10px; height: 6px; }
      .cva-upsell { text-align: center; padding: 12px 0 4px; }
      .cva-upsell strong { font-size: 16px; font-weight: 600; }
      .cva-upsell p { font-size: 12px; line-height: 1.6; color: var(--cva-muted); margin: 8px 0 12px; }
      .cva .cva-upgrade {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        background: var(--cva-text);
        color: var(--cva-surface);
        border-radius: 12px;
        padding: 0 20px;
        font-size: 13px;
        font-weight: 550;
      }

      .cva-stack-row { display: flex; align-items: center; gap: 14px; margin: 0 0 24px; }
      .cva .cva-stack { display: flex; align-items: center; padding-inline-end: 7px; flex-shrink: 0; max-width: 140px; }
      .cva-stack-count {
        display: grid;
        place-items: center;
        position: relative;
        z-index: 2;
        width: 32px;
        height: 32px;
        flex: 0 0 32px;
        border-radius: 50%;
        background: var(--cva-surface);
        border: 1px solid var(--cva-line);
        color: var(--cva-text);
        font-size: 11px;
        font-weight: 600;
        line-height: 1;
        margin-inline-start: 10px;
      }
      .cva-stack-copy { flex: 1; min-width: 0; }
      .cva-stack-copy strong { display: block; font-size: 12px; font-weight: 550; }
      .cva-stack-copy > span { display: block; font-size: 10px; color: var(--cva-muted); margin-top: 4px; line-height: 1.5; }
      .cva .cva-quick-upgrade {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        text-align: start;
        color: var(--cva-accent);
        font-size: 12px;
      }
      .cva .cva-full-button {
        background: var(--cva-text);
        color: var(--cva-surface);
        width: 100%;
        border-radius: 15px;
        min-height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 9px;
        font-size: 14px;
        font-weight: 550;
      }
      [dir="rtl"] .cva-full-button svg { transform: scaleX(-1); }

      .cva-skeleton { padding: 12px 0; }
      .cva-skeleton div { background: var(--cva-fill); border-radius: 12px; height: 90px; margin-bottom: 20px; }
      .cva-skeleton div:nth-child(2) { height: 120px; }
      .cva-message { padding: 50px 0; text-align: center; }
      .cva-message h2 { font-size: 19px; margin: 0 0 10px; font-weight: 600; }
      .cva-message p { font-size: 13px; color: var(--cva-muted); line-height: 1.6; margin: 0 0 12px; }
      .cva .cva-message button { background: var(--cva-fill); border-radius: 12px; padding: 0 20px; }

      .cva-section { margin-top: 32px; }
      .cva-recruiter { display: flex; align-items: center; gap: 8px; min-height: 44px; font-size: 13px; color: var(--cva-text); margin-top: 24px; }
      .cva-recruiter svg { color: var(--cva-muted); }
      .cva-breakdown { list-style: none; margin: 0; padding: 0; }
      .cva-breakdown li {
        display: grid;
        grid-template-columns: 32px minmax(0, 1fr) 72px 40px;
        align-items: center;
        gap: 12px;
        min-height: 52px;
        border-bottom: 1px solid var(--cva-line);
      }
      .cva-breakdown li:last-child { border: 0; }
      .cva-breakdown.is-keywords li { grid-template-columns: minmax(0, 1fr) 72px; }
      .cva-chip {
        width: 32px;
        height: 32px;
        border-radius: 9px;
        background: var(--cva-fill);
        color: var(--cva-muted);
        display: grid;
        place-items: center;
        font-size: 11px;
        font-weight: 600;
      }
      .cva-chip .cva-flag { font-size: 18px; line-height: 1; }
      .cva-breakdown-label { font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cva-breakdown-sub { display: block; font-size: 11px; color: var(--cva-muted); margin-top: 3px; }
      .cva-meter { height: 4px; border-radius: 4px; background: var(--cva-line); overflow: hidden; }
      .cva-meter span { display: block; height: 100%; border-radius: 4px; background: color-mix(in srgb, var(--cva-accent) 45%, var(--cva-surface)); }
      .cva-meter span.is-top { background: var(--cva-accent); }
      .cva-count { font-size: 12px; color: var(--cva-muted); text-align: end; font-variant-numeric: tabular-nums; }
      .cva-section-note { font-size: 12px; color: var(--cva-muted); margin: 0; padding: 12px 0; }
      .cva-engagement { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
      .cva-engagement strong { display: block; font-size: 22px; font-weight: 650; letter-spacing: -0.5px; font-variant-numeric: tabular-nums; }
      .cva-engagement span { display: block; font-size: 11px; color: var(--cva-muted); margin-top: 6px; }

      @media (max-width: 360px) {
        .cva-metrics { gap: 8px; }
        .cva-metric strong { font-size: 28px; }
        .cva-stack-row { gap: 10px; }
      }
      @media (prefers-reduced-motion: reduce) {
        .cva-bar, .cva-more svg { transition: none; }
      }
    `}</style>
  );
}

// ─── Icons ──────────────────────────────────────────────────────────

export function TabbioFavicon({ size = 15 }: { size?: number }) {
  return (
    <svg
      width={size * (24.0361 / 27.9998)}
      height={size}
      viewBox="0 0 24.0361 27.9998"
      fill="none"
      aria-hidden
      focusable="false"
    >
      <path d={TABBIO_MARK_PATH} fill="currentColor" />
    </svg>
  );
}

/**
 * The icon for a traffic source badge: monochrome in the badge's text colour,
 * decorative (the source is named beside it), never mirrored in RTL.
 */
export function TrafficSourceGlyph({ iconKey }: { iconKey: TrafficSourceIconKey }) {
  if (iconKey === 'share') {
    return <ShareIcon size={16} color="currentColor" aria-hidden focusable={false} />;
  }
  if (iconKey === 'tabbio') return <TabbioFavicon size={14} />;
  return <HugeIcon icon={TRAFFIC_SOURCE_HUGEICONS[iconKey]} size={16} color="currentColor" decorative />;
}

const ACTION_ICONS = { views: Eye, downloads: Download, shares: Share } as const;

type CvaActor = CvAnalyticsActivityView['actor'];

/** Stands in for activity the server did not send (Free placeholders). */
const ANONYMOUS_ACTOR: CvaActor = { kind: 'anonymous', id: null, name: '', imageUrl: null };

/**
 * People are circles, companies rounded squares (reference `.a-avatar`):
 * photo or logo when there is one, initials when it is missing or fails,
 * a neutral icon in the same shape for anonymous activity.
 */
function CvaAvatar({
  actor,
  variant,
  soft,
}: {
  actor: CvaActor;
  variant?: 'stack' | 'detail';
  soft?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  // Initials hold the place until the image has actually loaded: a host that
  // never answers fires no error, and a transparent logo must not show
  // letters through it once it is there.
  const [loaded, setLoaded] = useState(false);
  const company = actor.kind === 'company';
  const anonymous = actor.kind === 'anonymous';
  const initials = anonymous ? '' : actorInitials(actor.name);
  const showImage = !soft && !anonymous && Boolean(actor.imageUrl) && !failed;
  const className = [
    'cva-avatar',
    company ? 'is-company' : '',
    variant === 'stack' ? 'is-stack' : variant === 'detail' ? 'is-detail' : '',
    soft ? 'is-soft' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const Icon = company ? Building2 : User;
  return (
    <span className={className} aria-hidden>
      {soft || (showImage && loaded) ? null : anonymous || !initials ? (
        <Icon size={variant === 'stack' ? 15 : 20} strokeWidth={1.7} />
      ) : (
        <span className="cva-avatar-initials">{initials}</span>
      )}
      {showImage ? (
        <img
          src={actor.imageUrl!}
          alt=""
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          style={{ opacity: loaded ? 1 : 0 }}
        />
      ) : null}
    </span>
  );
}

// ─── Header ─────────────────────────────────────────────────────────

export function CvaHeader({
  title,
  leading,
  onClose,
  closeLabel,
}: {
  title: string;
  /** The phone drawer button on the full screen; the quick look has none. */
  leading?: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
}) {
  return (
    <header className="cva-header">
      {leading}
      <h1>{title}</h1>
      {onClose ? (
        <button type="button" className="cva-close" onClick={onClose} aria-label={closeLabel}>
          <X size={17} strokeWidth={1.7} aria-hidden />
        </button>
      ) : null}
    </header>
  );
}

// ─── Scope row + compact filter ─────────────────────────────────────

export function CvaScope({
  model,
  scope,
  onScopeChange,
}: {
  model: CvAnalyticsModel;
  scope: CvAnalyticsScope;
  onScopeChange: (patch: Partial<CvAnalyticsScope>) => void;
}) {
  return (
    <div className="cva-scope">
      <span className="cva-scope-name">
        <TabbioFavicon />
        {model.host ? (
          <span className="cva-scope-host" dir="ltr">
            {model.host}
          </span>
        ) : null}
      </span>
      <CvaFilter model={model} scope={scope} onScopeChange={onScopeChange} />
    </div>
  );
}

function CvaFilter({
  model,
  scope,
  onScopeChange,
}: {
  model: CvAnalyticsModel;
  scope: CvAnalyticsScope;
  onScopeChange: (patch: Partial<CvAnalyticsScope>) => void;
}) {
  const { t } = useTranslation('seeker');
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<{ up: boolean; maxHeight: number }>({
    up: false,
    maxHeight: 320,
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const changed = scope.range !== 'all' || scope.cvId !== null;

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const above = rect.top - 12;
      const below = window.innerHeight - rect.bottom - 12;
      const up = below < 260 && above > below;
      setPlacement({ up, maxHeight: Math.max(120, up ? above : below) });
    };
    place();
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Close the menu only, not the dialog around it.
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener('resize', place);
    document.addEventListener('pointerdown', onPointerDown);
    rootRef.current?.addEventListener('keydown', onKeyDown);
    const root = rootRef.current;
    return () => {
      window.removeEventListener('resize', place);
      document.removeEventListener('pointerdown', onPointerDown);
      root?.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const durationLabel = (range: (typeof CV_ANALYTICS_RANGES)[number]) =>
    range === 'all'
      ? t('cvAnalytics.filter.all')
      : t('cvAnalytics.filter.days', { count: Number.parseInt(range, 10) });

  return (
    <div
      ref={rootRef}
      className="cva-filters"
      onBlur={(event) => {
        const next = event.relatedTarget as Node | null;
        if (next && !rootRef.current?.contains(next)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="cva-filter-trigger"
        aria-label={`${t('cvAnalytics.filter.trigger')}: ${model.filterLabel}`}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{model.filterLabel}</span>
        <ChevronDown size={15} strokeWidth={1.7} aria-hidden />
      </button>
      {open ? (
        <div
          className="cva-filter-menu"
          role="group"
          aria-label={t('cvAnalytics.filter.group')}
          style={{
            top: placement.up ? 'auto' : '100%',
            bottom: placement.up ? '100%' : 'auto',
            maxHeight: placement.maxHeight,
          }}
        >
          <div className="cva-filter-group" role="group" aria-label={t('cvAnalytics.filter.duration')}>
            <span className="cva-filter-label">{t('cvAnalytics.filter.duration')}</span>
            <div className="cva-durations">
              {CV_ANALYTICS_RANGES.map((range) => (
                <button
                  key={range}
                  type="button"
                  className="cva-duration"
                  aria-pressed={scope.range === range}
                  aria-label={
                    range === 'all'
                      ? t('cvAnalytics.range.allTime')
                      : t('cvAnalytics.range.lastDays', { count: Number.parseInt(range, 10) })
                  }
                  onClick={() => onScopeChange({ range })}
                >
                  {durationLabel(range)}
                </button>
              ))}
            </div>
          </div>
          <div className="cva-filter-group" role="group" aria-label={t('cvAnalytics.filter.cvVersion')}>
            <label className="cva-cv-row">
              <span>{t('cvAnalytics.filter.cv')}</span>
              {/* The browser's own picker, per the handoff: no custom CV list. */}
              <select
                className="cva-cv-select"
                dir="auto"
                aria-label={t('cvAnalytics.filter.cvVersion')}
                value={scope.cvId ?? ''}
                onChange={(event) => onScopeChange({ cvId: event.target.value || null })}
              >
                <option value="">{t('cvAnalytics.filter.allCvs')}</option>
                {model.cvOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.title}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {changed ? (
            <button
              type="button"
              className="cva-filter-reset"
              onClick={() => {
                onScopeChange({ range: 'all', cvId: null });
                triggerRef.current?.focus();
              }}
            >
              {t('cvAnalytics.filter.reset')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ─── Metric tabs ────────────────────────────────────────────────────

export function CvaMetrics({
  model,
  metric,
  onChange,
}: {
  model: CvAnalyticsModel;
  metric: CvAnalyticsMetric;
  onChange: (metric: CvAnalyticsMetric) => void;
}) {
  const { t } = useTranslation('seeker');
  return (
    <div className="cva-metrics" role="group" aria-label={t('cvAnalytics.hub.metricGroup')}>
      {model.metrics.map((item) => {
        const flat = item.trend?.kind === 'change' && item.trend.direction !== 'up';
        return (
          <button
            key={item.key}
            type="button"
            className="cva-metric"
            aria-pressed={item.key === metric}
            aria-label={[item.label, item.value, item.trendAccessibilityLabel].filter(Boolean).join(', ')}
            onClick={() => onChange(item.key)}
          >
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <small
              className={flat ? 'cva-trend is-flat' : 'cva-trend'}
              aria-hidden
              // Hover says which windows the percentage compares.
              title={item.trendAccessibilityLabel ?? undefined}
            >
              {item.trendLabel ?? ''}
            </small>
          </button>
        );
      })}
    </div>
  );
}

// ─── Bar chart ──────────────────────────────────────────────────────

export function CvaChart({
  model,
  metric,
  compact,
}: {
  model: CvAnalyticsModel;
  metric: CvAnalyticsMetric;
  compact?: boolean;
}) {
  const { t } = useTranslation('seeker');
  const [selected, setSelected] = useState<number | null>(null);
  const { chart } = model;
  const count = chart.bars.length;
  const last = count - 1;
  const focus = selected ?? last;
  const label = t(`cvAnalytics.metric.${metric}`);

  // A new scope or metric draws a new chart; drop the old selection.
  useEffect(() => setSelected(null), [chart.startTime, chart.days, metric]);

  const pick = (clientX: number, element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = Math.min(0.9999, Math.max(0, (clientX - rect.left) / rect.width));
    setSelected(Math.floor(ratio * count));
  };

  return (
    <div className="cva-chart">
      {chart.total > 0 ? (
        <div
          className={selected === null ? 'cva-bars' : 'cva-bars is-selecting'}
          dir="ltr"
          role="slider"
          tabIndex={0}
          aria-label={t('cvAnalytics.hub.chart', { metric: label })}
          aria-valuemin={1}
          aria-valuemax={count}
          aria-valuenow={focus + 1}
          aria-valuetext={model.barLabel(focus)}
          onPointerMove={(event) => pick(event.clientX, event.currentTarget)}
          onPointerDown={(event) => pick(event.clientX, event.currentTarget)}
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse') setSelected(null);
          }}
          onBlur={() => setSelected(null)}
          onKeyDown={(event) => {
            const moves: Record<string, number> = {
              ArrowLeft: focus - 1,
              ArrowDown: focus - 1,
              ArrowRight: focus + 1,
              ArrowUp: focus + 1,
              Home: 0,
              End: last,
            };
            const next = moves[event.key];
            if (next === undefined) return;
            event.preventDefault();
            setSelected(Math.min(last, Math.max(0, next)));
          }}
        >
          {chart.bars.map((value, index) => (
            <span
              key={index}
              className={[
                'cva-track',
                index === last ? 'is-last' : '',
                index === selected ? 'is-selected' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span
                className="cva-bar"
                style={{ height: `${chart.max ? (value / chart.max) * 100 : 0}%` }}
              />
            </span>
          ))}
          {selected !== null ? (
            <span
              className="cva-tip"
              aria-hidden
              style={{ left: `${Math.min(88, Math.max(12, ((selected + 0.5) / count) * 100))}%` }}
            >
              {model.barLabel(selected)}
            </span>
          ) : null}
        </div>
      ) : (
        <div className="cva-chart-empty">{t(`cvAnalytics.hub.chartEmpty.${metric}`)}</div>
      )}
      <div className="cva-axis" dir="ltr">
        {model.axis.map((date, index) =>
          compact && index === 1 ? null : <span key={index}>{date}</span>,
        )}
      </div>
    </div>
  );
}

// ─── Activity feed (full screen) ────────────────────────────────────

export function CvaActivity({
  model,
  scope,
  onSelectCv,
}: {
  model: CvAnalyticsModel;
  scope: CvAnalyticsScope;
  onSelectCv: (cvId: string) => void;
}) {
  const { t } = useTranslation('seeker');
  const [expanded, setExpanded] = useState(false);
  const { premium, activity } = model;
  // Free carries one activity from the server; Premium pages the feed.
  const visible = premium
    ? activity.slice(0, expanded ? activity.length : CV_ANALYTICS_ACTIVITY_PAGE)
    : activity.slice(0, 1);
  // Free placeholders stand for activity the server did not send.
  const moreThanOne = model.totalEvents > 1;
  const showCv = scope.cvId === null;
  const upgrade = () => model.openUpgrade('personal_analytics.activity');
  const [detail, setDetail] = useState<CvaActor | null>(null);

  return (
    <section className="cva-activity">
      <header>
        <h2>{t('cvAnalytics.hub.activity')}</h2>
      </header>
      <ul>
        {visible.map((item) => {
          const Icon = ACTION_ICONS[item.metric];
          const place = showCv ? item.cv?.title ?? item.placeLabel : null;
          return (
            <li key={item.id}>
              {/* The row and the CV shortcut are separate buttons, never nested. */}
              <button
                type="button"
                className="cva-hit"
                aria-label={
                  premium
                    ? t('cvAnalytics.hub.openDetails', { name: item.title })
                    : t('cvAnalytics.hub.explorePremiumActivity')
                }
                onClick={premium ? () => setDetail(item.actor) : upgrade}
              />
              <CvaAvatar actor={item.actor} />
              <span className="cva-copy">
                <strong dir="auto">{item.title}</strong>
                <small>
                  {item.action}
                  {place ? ' · ' : ''}
                  {showCv && item.cv ? (
                    <button
                      type="button"
                      className="cva-cv-link"
                      dir="auto"
                      aria-label={t('cvAnalytics.hub.showCv', { cv: item.cv.title })}
                      onClick={() => onSelectCv(item.cv!.id)}
                    >
                      {item.cv.title}
                    </button>
                  ) : place ? (
                    <span>{place}</span>
                  ) : null}
                </small>
              </span>
              <span className="cva-meta">
                <Icon size={14} strokeWidth={1.7} aria-hidden />
                <time>{item.age}</time>
              </span>
            </li>
          );
        })}
      </ul>
      {visible.length === 0 ? (
        <p className="cva-activity-empty">
          {model.hasAnyActivity ? t('cvAnalytics.hub.noRecentActivity') : t('cvAnalytics.hub.noActivityYet')}
        </p>
      ) : null}
      {!premium && visible.length ? (
        <>
          {moreThanOne ? (
            <div className="cva-locked" aria-hidden>
              <div>
                <i />
                <span>
                  <b />
                  <em />
                </span>
              </div>
              <div>
                <i />
                <span>
                  <b />
                  <em />
                </span>
              </div>
            </div>
          ) : null}
          <CvaUpsell onUpgrade={upgrade} />
        </>
      ) : null}
      {premium && activity.length > CV_ANALYTICS_ACTIVITY_PAGE ? (
        <button
          type="button"
          className="cva-more"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? t('cvAnalytics.hub.showLess') : t('cvAnalytics.hub.showMore')}
          <ChevronDown size={15} strokeWidth={1.7} aria-hidden />
        </button>
      ) : null}
      {premium ? (
        <CvaActorSheet actor={detail} scope={scope} model={model} onClose={() => setDetail(null)} />
      ) : null}
    </section>
  );
}

// ─── Actor detail sheet ─────────────────────────────────────────────

/**
 * One source's activity in the page's scope (reference `analytics-detail.js`):
 * a member, a company, or every anonymous visitor as one group. Counts are
 * actions, not people; anonymous totals never point at an individual.
 */
function CvaActorSheet({
  actor,
  scope,
  model,
  onClose,
}: {
  actor: CvaActor | null;
  scope: CvAnalyticsScope;
  model: CvAnalyticsModel;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation('seeker');
  const direction = typeof i18n.dir === 'function' ? i18n.dir(i18n.language) : 'ltr';
  const query = usePublicProfileAnalyticsActor(
    actor ? { range: scope.range, cvId: scope.cvId, kind: actor.kind, id: actor.id } : null,
  );
  if (!actor) return null;
  const detail = query.data;
  const anonymous = actor.kind === 'anonymous';
  const title = anonymous ? t('cvAnalytics.hub.detail.anonymousTitle') : actor.name;
  const kindLine = anonymous
    ? t('cvAnalytics.hub.detail.anonymous')
    : actor.kind === 'company'
      ? t('cvAnalytics.hub.detail.company')
      : t('cvAnalytics.hub.detail.person');
  const note = anonymous
    ? t('cvAnalytics.hub.detail.anonymousNote')
    : actor.kind === 'company'
      ? t('cvAnalytics.hub.detail.companyNote')
      : null;
  const recent = (detail?.recentActivity ?? []).map(model.toActivityView);
  const totals = [
    { key: 'views', label: t('cvAnalytics.metric.views'), value: detail?.totals.views ?? 0 },
    { key: 'downloads', label: t('cvAnalytics.metric.downloads'), value: detail?.totals.downloads ?? 0 },
    { key: 'shares', label: t('cvAnalytics.metric.shares'), value: detail?.totals.shares ?? 0 },
  ];

  return (
    <WebDialog visible title={title} onClose={onClose} maxWidth={420} hideHeader>
      <div
        className="cva cva-detail"
        dir={direction}
        style={{ ['--cva-surface' as string]: 'var(--menu-bg)' }}
      >
        <header className="cva-detail-header">
          <span>{t('cvAnalytics.hub.detail.title')}</span>
          <button type="button" className="cva-close" aria-label={t('cvAnalytics.hub.detail.close')} onClick={onClose}>
            <X size={17} strokeWidth={1.7} aria-hidden />
          </button>
        </header>
        <div className="cva-detail-identity">
          <CvaAvatar actor={actor} variant="detail" />
          <div>
            <h2 dir="auto">{title}</h2>
            <p>{kindLine}</p>
          </div>
        </div>
        <p className="cva-detail-scope">
          <bdi>{model.selectedCvLabel ?? t('cvAnalytics.filter.allCvs')}</bdi> · {model.periodLabel}
        </p>
        {query.isLoading && !detail ? (
          <div className="cva-detail-state" aria-busy="true">
            {t('cvAnalytics.hub.loading')}
          </div>
        ) : query.gone ? (
          <div className="cva-detail-state" role="status">
            <p>{t('cvAnalytics.hub.detail.gone')}</p>
          </div>
        ) : query.error && !detail ? (
          <div className="cva-detail-state" role="alert">
            <p>{t('cvAnalytics.hub.detail.error')}</p>
            <button type="button" onClick={() => void query.refetch()}>
              {t('cvAnalytics.hub.tryAgain')}
            </button>
          </div>
        ) : (
          <>
            <div className="cva-detail-metrics">
              {totals.map((item) => (
                <div key={item.key}>
                  <strong>{model.fmt(item.value)}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
            {note ? <p className="cva-detail-note">{note}</p> : null}
            <section className="cva-detail-history">
              <h3>{t('cvAnalytics.hub.detail.recent')}</h3>
              <ul>
                {recent.map((item) => {
                  const Icon = ACTION_ICONS[item.metric];
                  const place = item.cv?.title ?? item.placeLabel;
                  return (
                    <li key={item.id}>
                      <Icon size={20} strokeWidth={1.7} aria-hidden />
                      <span>
                        <strong>{item.action}</strong>
                        {place ? <small dir="auto">{place}</small> : null}
                      </span>
                      <time>{item.age}</time>
                    </li>
                  );
                })}
              </ul>
              {recent.length === 0 ? (
                <p className="cva-detail-note">{t('cvAnalytics.hub.detail.noRecent')}</p>
              ) : null}
            </section>
            <p className="cva-detail-footnote">
              {actor.kind === 'company'
                ? t('cvAnalytics.hub.detail.companyFootnote')
                : t('cvAnalytics.hub.detail.footnote')}
            </p>
          </>
        )}
      </div>
    </WebDialog>
  );
}

export function CvaUpsell({ onUpgrade }: { onUpgrade: () => void }) {
  const { t } = useTranslation('seeker');
  return (
    <div className="cva-upsell">
      <strong>{t('cvAnalytics.hub.seeFullActivity')}</strong>
      <p>{t('cvAnalytics.hub.fullActivityPremium')}</p>
      <button type="button" className="cva-upgrade" onClick={onUpgrade}>
        <PremiumMark size={18} color="currentColor" />
        {t('cvAnalytics.hub.explorePremium')}
      </button>
    </div>
  );
}

// ─── Quick look: avatar stack + one line ────────────────────────────

export function CvaStack({
  model,
  onViewFull,
  onUpgrade,
}: {
  model: CvAnalyticsModel;
  onViewFull: () => void;
  onUpgrade: () => void;
}) {
  const { t } = useTranslation('seeker');
  const { premium, activity, totalEvents } = model;
  // Distinct sources, newest first. Free has the latest one clear and the
  // next two lightly obscured placeholders for activity it was not sent.
  const actors = selectStackActors(activity);
  // Employer views inside Tabbio are listed but left out of the totals, so
  // the feed can hold activity the totals do not count.
  const activityCount = Math.max(totalEvents, activity.length);
  const shown = Math.min(
    CV_ANALYTICS_STACK_SIZE,
    premium ? actors.length : activity.length ? activityCount : 0,
  );
  // Counts are activity, not people, and the server sends no source count,
  // so the overflow chip is an unnumbered "+" rather than a guess.
  const more = activityCount > shown;

  return (
    <div className="cva-stack-row">
      {shown ? (
        <button
          type="button"
          className="cva-stack"
          aria-label={
            premium ? t('cvAnalytics.hub.viewFullActivity') : t('cvAnalytics.hub.explorePremiumActivity')
          }
          onClick={premium ? onViewFull : onUpgrade}
        >
          {Array.from({ length: shown }, (_, index) => (
            <CvaAvatar
              // Keyed by who it is, so a new actor never inherits the last
              // one's image state after a filter change.
              key={actors[index] ? `${actors[index]!.actor.kind}:${actors[index]!.actor.id ?? 'anon'}` : `slot-${index}`}
              actor={actors[index]?.actor ?? ANONYMOUS_ACTOR}
              variant="stack"
              soft={!premium && index > 0}
            />
          ))}
          {more ? (
            <span className="cva-stack-count" aria-hidden>
              +
            </span>
          ) : null}
        </button>
      ) : null}
      <div className="cva-stack-copy">
        <strong>
          {activity.length ? t('cvAnalytics.hub.gettingNoticed') : t('cvAnalytics.hub.noRecentActivity')}
        </strong>
        {premium ? (
          activity.length ? <span>{t('cvAnalytics.hub.stackCaption')}</span> : null
        ) : (
          <button type="button" className="cva-quick-upgrade" onClick={onUpgrade}>
            <PremiumMark size={16} color="currentColor" />
            {t('cvAnalytics.hub.explorePremium')}
          </button>
        )}
      </div>
    </div>
  );
}

export function CvaFullButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="cva-full-button" onClick={onClick}>
      {label}
      <ArrowRight size={17} strokeWidth={1.7} aria-hidden />
    </button>
  );
}

// ─── Loading / error / live status ──────────────────────────────────

export function CvaLoading() {
  const { t } = useTranslation('seeker');
  return (
    <div className="cva-skeleton" aria-busy="true" aria-label={t('cvAnalytics.hub.loading')}>
      <div />
      <div />
      <div />
    </div>
  );
}

export function CvaError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('seeker');
  return (
    <div className="cva-message" role="alert">
      <h2>{t('cvAnalytics.hub.errorTitle')}</h2>
      <p>{t('cvAnalytics.hub.errorBody')}</p>
      <button type="button" onClick={onRetry}>
        {t('cvAnalytics.hub.tryAgain')}
      </button>
    </div>
  );
}

/**
 * Announces the new totals once a filter change has loaded, not on first
 * paint: the screen reader hears what the filter did, scoped to this panel.
 */
export function CvaLiveStatus({ model, scope }: { model: CvAnalyticsModel; scope: CvAnalyticsScope }) {
  const [message, setMessage] = useState('');
  const pending = useRef(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    pending.current = true;
  }, [scope.range, scope.cvId]);
  useEffect(() => {
    if (pending.current && model.data && !model.isUpdating) {
      pending.current = false;
      setMessage(model.liveSummary);
    }
  }, [model.data, model.isUpdating, model.liveSummary]);
  return (
    <div className="cva-sr-only" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
}

export function CvaSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="cva-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}
